import { Injectable } from '@angular/core';
import { State, Selector, Action, StateContext } from '@ngxs/store';
import { DEFAULT_PAGE_SIZE_OPTIONS, Error, CustomHttpErrorResponse } from '../../../../../common/constants';
import {
  TransportManagementTransportOrderLoad,
  TransportManagementTransportOrderInitialState,
} from './transport-management-transport-order.actions';
import { Observable, of } from 'rxjs';
import { TransportManagementTransportOrderService } from './transport-management-transport-order.service';
import { NotificationService } from '../../../../../common/services';
import { catchError, map } from 'rxjs/operators';
import { TransportManagementTile, TransportManagementTileItem } from '../models';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface TransportManagementTransportOrderStateModel {
  items: TransportManagementTileItem[];
  page: number;
  size: number;
  isMoreButtonVisible: boolean;
  loaded: boolean;
  isReportLimitationExceeded: boolean;
}

const initialState: TransportManagementTransportOrderStateModel = {
  items: [],
  page: 0,
  size: DEFAULT_PAGE_SIZE_OPTIONS[1],
  isMoreButtonVisible: false,
  loaded: false,
  isReportLimitationExceeded: false,
};

@State({
  name: 'transportManagementTransportOrder',
  defaults: initialState,
})
@Injectable()
export class TransportManagementTransportOrderState {
  @Selector()
  static getState(state: TransportManagementTransportOrderStateModel): TransportManagementTransportOrderStateModel {
    return state;
  }

  @Selector()
  static getItems(state: TransportManagementTransportOrderStateModel): TransportManagementTileItem[] {
    return state.items;
  }

  @Selector()
  static getLoaded(state: TransportManagementTransportOrderStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static isMoreButtonVisible(state: TransportManagementTransportOrderStateModel): boolean {
    return state.isMoreButtonVisible;
  }

  @Selector()
  static isReportLimitationExceeded(state: TransportManagementTransportOrderStateModel): boolean {
    return state.isReportLimitationExceeded;
  }

  constructor(
    private transportManagementTransportOrderService: TransportManagementTransportOrderService,
    private notificationService: NotificationService,
  ) {}

  @Action(TransportManagementTransportOrderLoad)
  getTransportOrderList(
    ctx: StateContext<TransportManagementTransportOrderStateModel>,
    { payload }: TransportManagementTransportOrderLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    const { page } = payload;
    return this.transportManagementTransportOrderService.getTransportManagementTransportOrderList(payload).pipe(
      map((list: TransportManagementTile) => {
        ctx.patchState({
          items: page ? ctx.getState().items.concat(list.items) : list.items,
          isMoreButtonVisible: !!list.items?.length,
          page,
          loaded: true,
          isReportLimitationExceeded: list.reportLimitationExceeded,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportManagementTransportOrderInitialState)
  setInitialState(ctx: StateContext<TransportManagementTransportOrderStateModel>): void {
    ctx.setState(initialState);
  }
}
